import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
const domainpath = process.env.REACT_APP_API_DOMAIN_ENDPOINT;

const TabbedSection = () => {
  const [data, setData] = useState({
    totalNFTs: 0,
    totalBundles: 0,
    activeAuctions: 0,
    uniqueCreators: 0,
    nftList: [],
    bundleList: [],
    auctionList: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("nfts");
  const [imageLoadErrors, setImageLoadErrors] = useState({});
  const navigate = useNavigate();
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(`${domainpath}/api/stats`);
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();
        setData(result);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleTabClick = (tab) => setActiveTab(tab);

  const handleImageError = (itemId, event) => {
    if (!imageLoadErrors[itemId]) {
      setImageLoadErrors((prev) => ({ ...prev, [itemId]: true }));
      event.target.onerror = null;
      event.target.src = "https://via.placeholder.com/300";
    }
  };

  const renderItemImage = (item) => {
    const { mime_type, ipfs_url } = item.value || {};
    const url = ipfs_url?.replace("ipfs://", "https://ipfs.io/ipfs/");

    if (!url || imageLoadErrors[item.id]) {
      return (
        <img
          src="https://via.placeholder.com/300"
          alt="Placeholder"
          className="object-cover w-full h-full rounded-lg"
        />
      );
    }

    if (mime_type?.startsWith("image/")) {
      return (
        <img
          src={url}
          alt={item.value?.name || "NFT Image"}
          className="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
          onError={(e) => handleImageError(item.id, e)}
        />
      );
    }

    if (mime_type?.startsWith("video/")) {
      return (
        <video
          src={url}
          controls
          className="object-cover w-full h-full rounded-lg"
          onError={(e) => handleImageError(item.id, e)}
        />
      );
    }

    if (mime_type?.startsWith("audio/")) {
      return (
        <div className="flex items-center justify-center h-full w-full bg-gray-200">
          <audio controls src={url} className="w-full px-2">
            Your browser does not support the audio element.
          </audio>
        </div>
      );
    }

    return (
      <img
        src="./img/items/dummy.jpg"
        alt="Unsupported"
        className="object-cover w-full h-full rounded-lg"
      />
    );
  };

  const shortenAddress = (address) => {
    return address ? `${address.slice(0, 5)}...${address.slice(-4)}` : "";
  };

  const renderTabContent = () => {
    const renderCard = (item, extraContent) => (
      <div
        key={item.id}
        className="bg-white rounded-lg shadow-md overflow-hidden"
      >
        <div
          className="bg-gray-200 h-60 overflow-hidden flex items-center justify-center rounded-t-lg"
          onClick={(e) => navigate(`/art/${item._id}`)}
        >
          {renderItemImage(item)}
        </div>
        <div className="p-4">
          <h4
            className="text-md font-semibold text-gray-800 truncate"
            onClick={(e) => navigate(`/art/${item._id}`)}
          >
            {item.value?.name || item.value?.description || "Untitled"}
          </h4>
          <p className="text-sm text-gray-600 truncate">
            {item.value?.description || "No description."}
          </p>
          {extraContent}
        </div>
      </div>
    );

    switch (activeTab) {
      case "nfts":
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">
              NFTs ({data.totalNFTs})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {data.nftList.map((nft) =>
                renderCard(
                  nft,
                  <div className="mt-2 text-gray-700">
                    Creator:{" "}
                    <span className="font-medium truncate">
                      {shortenAddress(nft.value?.creator)}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        );

      case "bundles":
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Bundles ({data.totalBundles})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {data.bundleList.map((bundle) =>
                renderCard(
                  bundle,
                  <p className="text-sm text-gray-600">
                    Bundled NFT IDs:{" "}
                    {bundle.value?.bundled_nft_ids?.join(", ") || "None"}
                  </p>
                )
              )}
            </div>
          </div>
        );

      case "auctions":
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">
              Active Auctions ({data.activeAuctions})
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {data.auctionList.map((auction) =>
                renderCard(
                  auction,
                  <div className="mt-2 text-gray-700">
                    Creator:{" "}
                    <span className="font-medium truncate">
                      {shortenAddress(auction.value?.creator)}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading || error) {
    return (
      <section className="jumbotron breadcumb no-bg">
        <div className="mainbreadcumb">
          <div className="container">
            <div>
              {loading ? "Loading data..." : `Error loading data: ${error}`}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="jumbotron breadcumb no-bg">
      <div className="mainbreadcumb">
        <div className="container py-8">
          <div className="sm:hidden mb-4">
            <select
              id="tabs"
              className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
              value={activeTab}
              onChange={(e) => handleTabClick(e.target.value)}
            >
              <option value="nfts">NFTs ({data.totalNFTs})</option>
              <option value="bundles">Bundles ({data.totalBundles})</option>
              <option value="auctions">Auctions ({data.activeAuctions})</option>
            </select>
          </div>

          <div className="hidden sm:block mb-6">
            <nav className="relative z-0 rounded-lg shadow flex divide-x divide-gray-200">
              {["nfts", "bundles", "auctions"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => handleTabClick(tab)}
                  className={`${
                    activeTab === tab
                      ? "bg-white text-gray-900"
                      : "text-gray-500 hover:text-gray-700"
                  } group relative min-w-0 flex-1 overflow-hidden py-4 px-4 text-sm font-medium text-center focus:z-10`}
                  aria-current={activeTab === tab ? "page" : undefined}
                >
                  <span>
                    {tab === "nfts" && `NFTs (${data.totalNFTs})`}
                    {tab === "bundles" && `Bundles (${data.totalBundles})`}
                    {tab === "auctions" && `Auctions (${data.activeAuctions})`}
                  </span>
                  <span
                    aria-hidden="true"
                    className={`${
                      activeTab === tab ? "bg-indigo-500" : "bg-transparent"
                    } absolute inset-x-0 bottom-0 h-0.5`}
                  />
                </button>
              ))}
            </nav>
          </div>

          {renderTabContent()}
        </div>
      </div>
    </section>
  );
};

export default TabbedSection;
